import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, CircularProgress, Alert, Select, MenuItem,
  TextField, IconButton, Tooltip, Snackbar, InputAdornment, Tabs, Tab, Stack,
  TableSortLabel,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Button } from '@mui/material';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.superchargeme.com';

// Treatment options are jurisdiction-scoped: which options a row offers depends on
// its entity's country (UK→VAT, AU/SG→GST, US→sales/use). Matches the backend
// TREATMENTS_BY_COUNTRY in vat-vendor-rules-service.js.
const TREATMENTS_BY_COUNTRY = {
  UK: [
    { value: 'unknown', label: 'Not reviewed yet', color: 'default' },
    { value: 'uk_standard_20', label: '20% UK Standard', color: 'success' },
    { value: 'uk_reduced_5', label: '5% UK Reduced', color: 'info' },
    { value: 'uk_zero_rated', label: 'Zero-rated (0%)', color: 'warning' },
    { value: 'uk_exempt', label: 'Exempt (no VAT)', color: 'default' },
    { value: 'reverse_charge', label: 'Reverse charge (self-account)', color: 'secondary' },
    { value: 'outside_scope', label: 'Outside scope', color: 'default' },
  ],
  AU: [
    { value: 'unknown', label: 'Not reviewed yet', color: 'default' },
    { value: 'au_gst_10', label: 'GST 10%', color: 'success' },
    { value: 'au_gst_free', label: 'GST-free (0%)', color: 'warning' },
    { value: 'au_input_taxed', label: 'Input-taxed', color: 'default' },
    { value: 'reverse_charge', label: 'Reverse charge', color: 'secondary' },
    { value: 'outside_scope', label: 'Outside scope', color: 'default' },
  ],
  US: [
    { value: 'unknown', label: 'Not reviewed yet', color: 'default' },
    { value: 'us_taxable', label: 'Taxable', color: 'success' },
    { value: 'us_exempt', label: 'Exempt', color: 'default' },
    { value: 'us_outside_scope', label: 'Outside scope', color: 'default' },
  ],
  SG: [
    { value: 'unknown', label: 'Not reviewed yet', color: 'default' },
    { value: 'sg_gst_9', label: 'GST 9%', color: 'success' },
    { value: 'sg_gst_zero', label: 'Zero-rated (0%)', color: 'warning' },
    { value: 'sg_exempt', label: 'Exempt', color: 'default' },
    { value: 'outside_scope', label: 'Outside scope', color: 'default' },
  ],
};
// Flat value→{label,color} lookup across all jurisdictions (for chips/labels).
const TREATMENT_META = {};
Object.values(TREATMENTS_BY_COUNTRY).flat().forEach(t => { TREATMENT_META[t.value] = t; });
function treatmentsFor(country) {
  return TREATMENTS_BY_COUNTRY[String(country || 'UK').toUpperCase()] || TREATMENTS_BY_COUNTRY.UK;
}

const COUNTRY_FLAG = { UK: '🇬🇧', AU: '🇦🇺', US: '🇺🇸', SG: '🇸🇬' };
const CCY_LOCALE = { GBP: 'en-GB', AUD: 'en-AU', USD: 'en-US', SGD: 'en-SG' };
function fmtMoney(n, currency = 'GBP') {
  const ccy = currency || 'GBP';
  return n ? new Intl.NumberFormat(CCY_LOCALE[ccy] || 'en-GB', { style: 'currency', currency: ccy, maximumFractionDigits: 0 }).format(Number(n)) : '-';
}

function potentialReclaim(row) {
  if (row.decided_treatment === 'uk_standard_20' && row.bills_with_tax === 0) {
    return Math.round(Number(row.total_amt) / 6);
  }
  if (row.decided_treatment === 'reverse_charge' && row.bills_with_tax === 0) {
    return 0;
  }
  return null;
}

// =============================================================================
// Pre-flight banner — shows whether the splitter is ready to be enabled
// =============================================================================
function PreflightBanner({ preflight, onRefresh }) {
  if (!preflight) return null;
  const ready = preflight.vat_accounts_seeded && preflight.vendor_rules_count > 0;
  const flagOn = preflight.flag_enabled;
  let severity, icon, title, body;

  if (!preflight.vat_accounts_seeded) {
    severity = 'error';
    icon = <ErrorIcon />;
    title = 'VAT splitter NOT ready — chart of accounts incomplete';
    body = `Missing vat_role accounts: ${preflight.vat_accounts_missing.join(', ')}. Seed these in chart_of_accounts before enabling VAT_SPLIT_ENABLED.`;
  } else if (!ready) {
    severity = 'warning';
    icon = <WarningIcon />;
    title = 'VAT splitter scaffolded — needs vendor curation';
    body = 'All 7 VAT accounts are seeded, but vat_vendor_rules has no decisions. Curate AI-classifier rule_names below before enabling the flag.';
  } else if (!flagOn) {
    severity = 'info';
    icon = <CheckCircleIcon />;
    title = 'VAT splitter ready — flag is OFF';
    body = `${preflight.vendor_rules_count} vendor rule${preflight.vendor_rules_count === 1 ? '' : 's'} curated. Set VAT_SPLIT_ENABLED=true in backend/.env and restart data-platform-backend to activate.`;
  } else {
    severity = 'success';
    icon = <CheckCircleIcon />;
    title = 'VAT splitter ACTIVE';
    body = `Flag ON. ${preflight.vendor_rules_count} vendor rules. Splitting on: ${preflight.wired_generators.join(', ')}.`;
  }

  return (
    <Alert
      severity={severity}
      icon={icon}
      action={<IconButton size="small" onClick={onRefresh}><RefreshIcon /></IconButton>}
      sx={{ mb: 2 }}
    >
      <Typography variant="subtitle2">{title}</Typography>
      <Typography variant="body2">{body}</Typography>
      <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
        <Chip size="small" label={`Accounts: ${preflight.vat_accounts_present.length}/7`} color={preflight.vat_accounts_seeded ? 'success' : 'error'} />
        <Chip size="small" label={`Rules: ${preflight.vendor_rules_count}`} color={preflight.vendor_rules_count > 0 ? 'success' : 'default'} />
        <Chip size="small" label={`Flag: ${flagOn ? 'ON' : 'OFF'}`} color={flagOn ? 'success' : 'default'} />
        <Chip size="small" label={`Wired: ${preflight.wired_generators.length} generator${preflight.wired_generators.length === 1 ? '' : 's'}`} variant="outlined" />
      </Stack>
    </Alert>
  );
}

// =============================================================================
// QB Vendors tab — original review, keyed on QB vendor_ref_name
// =============================================================================
function QBVendorsTab({ rows, loading, error, notesMap, setNotesMap, saveRule, subsidiaries }) {
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [subFilter, setSubFilter] = useState('all');
  const [sortBy, setSortBy] = useState('total_amt');
  const [sortDir, setSortDir] = useState('desc');
  const rowKey = (r) => `${r.vendor_name}__${r.subsidiary_id}`;

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir(typeof rows[0]?.[col] === 'number' ? 'desc' : 'asc'); }
  };

  const filtered = rows.filter(r => {
    const matchesSearch = !filter || r.vendor_name.toLowerCase().includes(filter.toLowerCase());
    const matchesStatus = statusFilter === 'all'
      ? true
      : statusFilter === 'reviewed'
      ? r.decided_treatment !== 'unknown'
      : r.decided_treatment === 'unknown';
    const matchesSub = subFilter === 'all' || String(r.subsidiary_id) === String(subFilter);
    return matchesSearch && matchesStatus && matchesSub;
  }).slice().sort((a, b) => {
    const av = a[sortBy], bv = b[sortBy];
    let cmp;
    if (av == null && bv == null) cmp = 0;
    else if (av == null) cmp = 1;
    else if (bv == null) cmp = -1;
    else if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
    else cmp = String(av).localeCompare(String(bv));
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const stats = {
    total: filtered.length,
    reviewed: filtered.filter(r => r.decided_treatment !== 'unknown').length,
    potential_reclaim: filtered
      .filter(r => r.inferred_treatment === 'uk_standard_20' || r.inferred_treatment === 'reverse_charge')
      .reduce((sum, r) => sum + (potentialReclaim(r) || 0), 0),
  };
  // Spend only sums meaningfully within a single entity (one currency).
  const selCur = subFilter !== 'all'
    ? ((subsidiaries.find(s => String(s.id) === String(subFilter)) || {}).currency || 'GBP')
    : null;
  const spendInCur = selCur ? filtered.reduce((s, r) => s + Number(r.total_amt || 0), 0) : null;

  if (loading) return (<Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>);
  if (error) return (<Alert severity="error" sx={{ m: 2 }}>{error}</Alert>);

  return (
    <>
      <Alert severity="info" sx={{ mb: 2 }}>
        Vendors come from <code>quickbooks.quickbooks_bills</code>, one row per <strong>vendor × legal entity</strong>
        (each entity's own QB realm is the authoritative owner of the cost). Decisions save to <code>vat_vendor_rules</code>
        keyed on <code>(vendor_ref_name, subsidiary_id)</code>. The available tax treatments depend on the entity's
        country — UK&nbsp;VAT, AU&nbsp;GST, US&nbsp;sales/use, SG&nbsp;GST.
        <br/>
        Rows tagged <code>migration_auto</code> were seeded from the UK decision and <strong>need review</strong> for that jurisdiction.
      </Alert>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Chip label={`${stats.total} vendor×entity rows`} />
        <Chip label={`${stats.reviewed} reviewed`} color={stats.reviewed === stats.total ? 'success' : 'default'} />
        <Chip label={`${stats.total - stats.reviewed} pending`} color={stats.total - stats.reviewed === 0 ? 'success' : 'warning'} />
        {selCur && <Chip label={`Total spend ${fmtMoney(spendInCur, selCur)}`} />}
        {stats.potential_reclaim > 0 && <Chip label={`Potential UK reclaim ${fmtMoney(stats.potential_reclaim, 'GBP')}`} color="secondary" />}
      </Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Filter by vendor name..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          size="small"
          sx={{ minWidth: 300 }}
          InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }}
        />
        <Select value={subFilter} onChange={(e) => setSubFilter(e.target.value)} size="small" sx={{ minWidth: 200 }}>
          <MenuItem value="all">All entities</MenuItem>
          {subsidiaries.map(s => (
            <MenuItem key={s.id} value={String(s.id)}>{COUNTRY_FLAG[s.country] || ''} {s.name}</MenuItem>
          ))}
        </Select>
        <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} size="small">
          <MenuItem value="all">All vendors</MenuItem>
          <MenuItem value="reviewed">Reviewed only</MenuItem>
          <MenuItem value="not-reviewed">Not reviewed yet</MenuItem>
        </Select>
      </Box>
      <Card>
        <CardContent sx={{ p: 0 }}>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sortDirection={sortBy === 'subsidiary_name' ? sortDir : false}>
                    <TableSortLabel active={sortBy === 'subsidiary_name'} direction={sortBy === 'subsidiary_name' ? sortDir : 'asc'} onClick={() => handleSort('subsidiary_name')}>Entity</TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortBy === 'vendor_name' ? sortDir : false}>
                    <TableSortLabel active={sortBy === 'vendor_name'} direction={sortBy === 'vendor_name' ? sortDir : 'asc'} onClick={() => handleSort('vendor_name')}>Vendor</TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={sortBy === 'bills' ? sortDir : false}>
                    <TableSortLabel active={sortBy === 'bills'} direction={sortBy === 'bills' ? sortDir : 'desc'} onClick={() => handleSort('bills')}>Bills</TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={sortBy === 'total_amt' ? sortDir : false}>
                    <TableSortLabel active={sortBy === 'total_amt'} direction={sortBy === 'total_amt' ? sortDir : 'desc'} onClick={() => handleSort('total_amt')}>Total Spend</TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={sortBy === 'total_vat_claimed' ? sortDir : false}>
                    <TableSortLabel active={sortBy === 'total_vat_claimed'} direction={sortBy === 'total_vat_claimed' ? sortDir : 'desc'} onClick={() => handleSort('total_vat_claimed')}>Historical VAT</TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={sortBy === 'avg_rate_pct' ? sortDir : false}>
                    <TableSortLabel active={sortBy === 'avg_rate_pct'} direction={sortBy === 'avg_rate_pct' ? sortDir : 'desc'} onClick={() => handleSort('avg_rate_pct')}>Avg Rate</TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortBy === 'inferred_treatment' ? sortDir : false}>
                    <TableSortLabel active={sortBy === 'inferred_treatment'} direction={sortBy === 'inferred_treatment' ? sortDir : 'asc'} onClick={() => handleSort('inferred_treatment')}>Inferred</TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ minWidth: 200 }} sortDirection={sortBy === 'decided_treatment' ? sortDir : false}>
                    <TableSortLabel active={sortBy === 'decided_treatment'} direction={sortBy === 'decided_treatment' ? sortDir : 'asc'} onClick={() => handleSort('decided_treatment')}>Treatment</TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ minWidth: 200 }}>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map(r => {
                  const k = rowKey(r);
                  const currentTreatment = r.decided_treatment || 'unknown';
                  const tObj = TREATMENT_META[currentTreatment];
                  const opts = treatmentsFor(r.country);
                  return (
                    <TableRow key={k} hover>
                      <TableCell>
                        <Chip size="small" variant="outlined"
                          label={`${COUNTRY_FLAG[r.country] || ''} ${r.subsidiary_name || r.country}`} />
                      </TableCell>
                      <TableCell>
                        <strong>{r.vendor_name}</strong>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {r.first_seen} → {r.last_seen}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{r.bills}</TableCell>
                      <TableCell align="right">{fmtMoney(r.total_amt, r.currency)}</TableCell>
                      <TableCell align="right">
                        {fmtMoney(r.total_vat_claimed, r.currency)}
                        {r.bills_missing_tax_big > 0 && (
                          <Typography variant="caption" display="block" color="warning.main">
                            {r.bills_missing_tax_big}/{r.bills} w/o tax
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">{r.avg_rate_pct}%</TableCell>
                      <TableCell>
                        <Chip size="small" label={r.inferred_treatment} variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Select
                          value={currentTreatment}
                          onChange={(e) => saveRule(r.vendor_name, r.subsidiary_id, e.target.value, notesMap[k] || '')}
                          size="small"
                          fullWidth
                          sx={{ bgcolor: (!tObj || tObj.color === 'default') ? 'background.default' : `${tObj.color}.50` }}
                        >
                          {opts.map(t => (
                            <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="Add notes..."
                          value={notesMap[k] || ''}
                          onChange={(e) => setNotesMap(m => ({ ...m, [k]: e.target.value }))}
                          onBlur={() => {
                            if (currentTreatment !== 'unknown') {
                              saveRule(r.vendor_name, r.subsidiary_id, currentTreatment, notesMap[k] || '');
                            }
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </>
  );
}

// =============================================================================
// Classifier Coverage tab — Amex + Revolut classifier rule_names with VAT status
// =============================================================================
function ClassifierCoverageTab({ rows, loading, error, notesMap, setNotesMap, saveRule, llmRecs, setLlmRecs, llmLoading, fetchLlmRecs }) {
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recent_volume');
  const [sortDir, setSortDir] = useState('desc');

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir(typeof rows[0]?.[col] === 'number' ? 'desc' : 'asc'); }
  };

  const filtered = rows.filter(r => {
    const matchesSearch = !filter
      || r.rule_name.toLowerCase().includes(filter.toLowerCase())
      || (r.match_value || '').toLowerCase().includes(filter.toLowerCase());
    const reviewed = r.treatment !== 'unknown';
    const matchesStatus = statusFilter === 'all'
      ? true
      : statusFilter === 'reviewed'
      ? reviewed
      : !reviewed;
    return matchesSearch && matchesStatus;
  }).slice().sort((a, b) => {
    const av = a[sortBy], bv = b[sortBy];
    let cmp;
    if (av == null && bv == null) cmp = 0;
    else if (av == null) cmp = 1;
    else if (bv == null) cmp = -1;
    else if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
    else cmp = String(av).localeCompare(String(bv));
    return sortDir === 'asc' ? cmp : -cmp;
  });

  // rule_name is not unique — count the rules carrying each name so a row can say so.
  // useMemo + mutate-in-place: the object-spread form allocated ~366 intermediate objects of
  // ~364 keys each and re-ran on every render, including every keystroke in a Notes field and
  // every sort click, on the largest table in this view.
  const rulesPerName = useMemo(
    () => rows.reduce((m, r) => { m[r.rule_name] = (m[r.rule_name] || 0) + 1; return m; }, {}),
    [rows]
  );

  const stats = {
    total: rows.length,
    // `rows` is ONE ROW PER RULE, so rows.length is a rule count, not a name count. The chip
    // below is labelled "classifier rule_names", which currently over-reads by the number of
    // namesake rules (2 today: Anthropic AI 2016/2260, HMRC Tax Payment 2018/2259). That is a
    // smaller instance of the exact name-vs-rule conflation this change exists to fix, in the
    // same file, so it is corrected here rather than left to contradict the fix beside it.
    distinctNames: new Set(rows.map(r => r.rule_name)).size,
    reviewed: rows.filter(r => r.treatment !== 'unknown').length,
    recent_volume: rows.reduce((s, r) => s + Number(r.recent_volume || 0), 0),
    recent_volume_unclassified: rows
      .filter(r => r.treatment === 'unknown')
      .reduce((s, r) => s + Number(r.recent_volume || 0), 0),
  };

  if (loading) return (<Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>);
  if (error) return (<Alert severity="error" sx={{ m: 2 }}>{error}</Alert>);

  return (
    <>
      <Alert severity="info" sx={{ mb: 2 }}>
        These are the AI-classifier <code>rule_name</code>s that the bank-transaction processor uses to categorize
        Amex, Revolut, Wise, Mercury and Starling transactions. The VAT splitter looks up <code>vat_vendor_rules</code>{' '}
        using the rule_name as the key — so a rule_name without a treatment here will silently passthrough when the
        splitter is enabled.
        <br/>
        <strong>Volume (last 365d)</strong> = transactions in the last 365 calendar days (rolling, today-anchored — not the VAT quarter)
        across all bank sources. Use it to prioritize which rule_names to classify first. Each transaction is counted once, against
        the single highest-priority <em>regex</em> rule that matched it — rules matched on an exact value or a merchant category
        carry no volume figure here, because the volume query only scans regex rules — so a dash means no transaction's winning rule was this one (either nothing
        matched its pattern, or a higher-priority rule got there first). One row per <em>rule</em>: a rule_name carrying several
        active rules appears once per rule, and each shows its own GL, priority and volume.
      </Alert>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Chip label={`${stats.total} classifier rules · ${stats.distinctNames} rule_names`} />
        <Chip label={`${stats.reviewed} reviewed`} color={stats.reviewed === stats.total ? 'success' : 'default'} />
        <Chip label={`${stats.total - stats.reviewed} unclassified`} color={stats.total === stats.reviewed ? 'success' : 'warning'} />
        <Chip label={`Unclassified volume (last 365d): ${fmtMoney(stats.recent_volume_unclassified)}`} color="warning" />
        <Chip label={`Total volume (last 365d): ${fmtMoney(stats.recent_volume)}`} />
      </Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          placeholder="Filter by rule_name or match pattern..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          size="small"
          sx={{ minWidth: 320 }}
          InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }}
        />
        <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} size="small">
          <MenuItem value="all">All rules</MenuItem>
          <MenuItem value="reviewed">Reviewed only</MenuItem>
          <MenuItem value="not-reviewed">Unclassified only</MenuItem>
        </Select>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="outlined"
          size="small"
          startIcon={llmLoading ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
          onClick={fetchLlmRecs}
          disabled={llmLoading}
        >
          {llmLoading ? 'Asking Claude...' : (Object.keys(llmRecs).length > 0 ? 'Refresh AI Suggestions' : 'Get AI Suggestions')}
        </Button>
      </Box>
      <Card>
        <CardContent sx={{ p: 0 }}>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sortDirection={sortBy === 'rule_name' ? sortDir : false}>
                    <TableSortLabel active={sortBy === 'rule_name'} direction={sortBy === 'rule_name' ? sortDir : 'asc'} onClick={() => handleSort('rule_name')}>Rule name (lookup key)</TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortBy === 'match_value' ? sortDir : false}>
                    <TableSortLabel active={sortBy === 'match_value'} direction={sortBy === 'match_value' ? sortDir : 'asc'} onClick={() => handleSort('match_value')}>Pattern</TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={sortBy === 'recent_tx_count' ? sortDir : false}>
                    <TableSortLabel active={sortBy === 'recent_tx_count'} direction={sortBy === 'recent_tx_count' ? sortDir : 'desc'} onClick={() => handleSort('recent_tx_count')}>Tx (last 365d)</TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={sortBy === 'recent_volume' ? sortDir : false}>
                    <TableSortLabel active={sortBy === 'recent_volume'} direction={sortBy === 'recent_volume' ? sortDir : 'desc'} onClick={() => handleSort('recent_volume')}>Volume (last 365d)</TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ minWidth: 220 }}>AI Suggestion</TableCell>
                  <TableCell sx={{ minWidth: 200 }} sortDirection={sortBy === 'treatment' ? sortDir : false}>
                    <TableSortLabel active={sortBy === 'treatment'} direction={sortBy === 'treatment' ? sortDir : 'asc'} onClick={() => handleSort('treatment')}>VAT Treatment</TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ minWidth: 200 }}>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map(r => {
                  const currentTreatment = r.treatment || 'unknown';
                  const tObj = TREATMENT_META[currentTreatment];
                  const aiRec = llmRecs[r.rule_name];
                  const aiTreatmentObj = aiRec ? TREATMENT_META[aiRec.treatment] : null;
                  const namesakes = rulesPerName[r.rule_name] || 1;
                  return (
                    <TableRow key={r.rule_id} hover>
                      <TableCell>
                        <strong>{r.rule_name}</strong>
                        <Typography variant="caption" display="block" color="text.secondary">
                          GL {r.gl_account} · priority {r.priority} · rule #{r.rule_id}
                        </Typography>
                        {namesakes > 1 && (
                          <Typography variant="caption" display="block" color="warning.main">
                            {namesakes} active rules share this rule_name — they share ONE VAT
                            treatment, and volume is shown against whichever rule wins the match.
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <code style={{ fontSize: '0.75rem' }}>{r.match_value}</code>
                      </TableCell>
                      <TableCell align="right">{r.recent_tx_count || '-'}</TableCell>
                      <TableCell align="right">{fmtMoney(r.recent_volume)}</TableCell>
                      <TableCell>
                        {aiRec ? (
                          <Stack spacing={0.5}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip
                                size="small"
                                label={aiTreatmentObj?.label || aiRec.treatment}
                                color={aiTreatmentObj?.color || 'default'}
                                variant="outlined"
                              />
                              <Typography variant="caption" color="text.secondary">{aiRec.confidence}%</Typography>
                              {currentTreatment === 'unknown' && (
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => {
                                    const aiNote = `[AI ${aiRec.confidence}%]: ${aiRec.reasoning}`;
                                    const existing = notesMap[r.rule_name] || '';
                                    const merged = existing ? `${aiNote}\n${existing}` : aiNote;
                                    setNotesMap(m => ({ ...m, [r.rule_name]: merged }));
                                    saveRule(r.rule_name, undefined, aiRec.treatment, merged);
                                  }}
                                  sx={{ minWidth: 0, p: '2px 6px' }}
                                >
                                  Apply
                                </Button>
                              )}
                            </Stack>
                            <Tooltip title={aiRec.reasoning} placement="top">
                              <Typography variant="caption" color="text.secondary" sx={{
                                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                                overflow: 'hidden', cursor: 'help',
                              }}>
                                {aiRec.reasoning}
                              </Typography>
                            </Tooltip>
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Select
                          value={currentTreatment}
                          onChange={(e) => saveRule(r.rule_name, undefined, e.target.value, notesMap[r.rule_name] || '')}
                          size="small"
                          fullWidth
                          sx={{ bgcolor: (!tObj || tObj.color === 'default') ? 'background.default' : `${tObj.color}.50` }}
                        >
                          {treatmentsFor('UK').map(t => (
                            <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="Add notes..."
                          value={notesMap[r.rule_name] || ''}
                          onChange={(e) => setNotesMap(m => ({ ...m, [r.rule_name]: e.target.value }))}
                          onBlur={() => {
                            if (currentTreatment !== 'unknown') {
                              saveRule(r.rule_name, undefined, currentTreatment, notesMap[r.rule_name] || '');
                            }
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </>
  );
}

// =============================================================================
// Main component
// =============================================================================
export default function VATVendorReview() {
  const [tab, setTab] = useState(0);
  const [preflight, setPreflight] = useState(null);
  const [qbRows, setQbRows] = useState([]);
  const [classifierRows, setClassifierRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [notesMap, setNotesMap] = useState({});
  const [llmRecs, setLlmRecs] = useState({});
  const [llmLoading, setLlmLoading] = useState(false);
  const [subsidiaries, setSubsidiaries] = useState([]);

  const token = () => localStorage.getItem('token');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = { Authorization: `Bearer ${token()}` };
      const [preflightRes, qbRes, classifierRes, subsRes] = await Promise.all([
        axios.get(`${API_URL}/api/accounting/vat-preview/preflight`, { headers }),
        axios.get(`${API_URL}/api/accounting/vat-preview/vendor-rules?from=2023-01-01&to=2026-12-31`, { headers }),
        axios.get(`${API_URL}/api/accounting/vat-preview/classifier-coverage?days_back=365`, { headers }),
        axios.get(`${API_URL}/api/accounting/vat-preview/subsidiaries`, { headers }),
      ]);
      if (preflightRes.data.success) setPreflight(preflightRes.data.data);
      if (subsRes.data.success) setSubsidiaries(subsRes.data.data);
      const nm = {};
      if (qbRes.data.success) {
        setQbRows(qbRes.data.data);
        for (const r of qbRes.data.data) if (r.notes) nm[`${r.vendor_name}__${r.subsidiary_id}`] = r.notes;
      } else setError(qbRes.data.error);
      if (classifierRes.data.success) {
        setClassifierRows(classifierRes.data.data);
        for (const r of classifierRes.data.data) if (r.notes) nm[r.rule_name] = r.notes;
      }
      setNotesMap(nm);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const fetchLlmRecs = async () => {
    const unclassified = classifierRows.filter(r => r.treatment === 'unknown');
    if (unclassified.length === 0) {
      setToast({ msg: 'All classifier rule_names are already classified — nothing to recommend.', severity: 'info' });
      return;
    }
    setLlmLoading(true);
    try {
      const payload = unclassified.map(r => ({
        rule_name: r.rule_name,
        match_value: r.match_value,
        gl_account: r.gl_account,
      }));
      const res = await axios.post(`${API_URL}/api/accounting/vat-preview/classifier-recommend`, {
        rule_names: payload,
        force_refresh: false,
      }, {
        headers: { Authorization: `Bearer ${token()}` },
        timeout: 180000,  // 3 min — Opus 4.7 with adaptive thinking can take 60-90s
      });
      if (res.data.success) {
        setLlmRecs(res.data.data);
        const got = Object.keys(res.data.data).length;
        const missing = unclassified.length - got;
        setToast({
          msg: missing > 0
            ? `AI suggested ${got} treatments (${missing} omitted by model)`
            : `AI suggested treatments for ${got} rule_names`,
          severity: 'success',
        });
      } else {
        setToast({ msg: res.data.error || 'AI suggestion failed', severity: 'error' });
      }
    } catch (e) {
      setToast({ msg: e.response?.data?.error || e.message, severity: 'error' });
    } finally {
      setLlmLoading(false);
    }
  };

  const saveRule = async (vendor, subsidiary_id, treatment, notes) => {
    try {
      const res = await axios.post(`${API_URL}/api/accounting/vat-preview/vendor-rules`, {
        vendor, subsidiary_id, treatment, notes: notes || ''
      }, { headers: { Authorization: `Bearer ${token()}` } });
      if (res.data.success) {
        setToast({ msg: `Saved: ${vendor} → ${treatment}`, severity: 'success' });
        // QB tab rows are per (vendor, subsidiary); classifier rows are keyed on rule_name (treasury/UK).
        if (subsidiary_id != null) {
          setQbRows(rs => rs.map(r => (r.vendor_name === vendor && String(r.subsidiary_id) === String(subsidiary_id)) ? { ...r, decided_treatment: treatment } : r));
        }
        setClassifierRows(rs => rs.map(r => r.rule_name === vendor ? { ...r, treatment: treatment } : r));
        // Refresh preflight (rule count changes)
        try {
          const pf = await axios.get(`${API_URL}/api/accounting/vat-preview/preflight`, {
            headers: { Authorization: `Bearer ${token()}` }
          });
          if (pf.data.success) setPreflight(pf.data.data);
        } catch (_) {}
      } else {
        setToast({ msg: res.data.error, severity: 'error' });
      }
    } catch (e) {
      setToast({ msg: e.response?.data?.error || e.message, severity: 'error' });
    }
  };

  const unclassifiedCount = classifierRows.filter(r => r.treatment === 'unknown').length;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>Vendor Tax Classifiers (VAT / GST)</Typography>
        <Tooltip title="Refresh"><IconButton onClick={load}><RefreshIcon /></IconButton></Tooltip>
      </Box>

      <PreflightBanner preflight={preflight} onRefresh={load} />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label={`QB Bills Vendors (${qbRows.length})`} />
        <Tab label={
          <Stack direction="row" spacing={1} alignItems="center">
            <span>Amex/Bank Classifier ({classifierRows.length})</span>
            {unclassifiedCount > 0 && <Chip size="small" label={unclassifiedCount} color="warning" />}
          </Stack>
        } />
      </Tabs>

      {tab === 0 && (
        <QBVendorsTab
          rows={qbRows}
          loading={loading}
          error={error}
          notesMap={notesMap}
          setNotesMap={setNotesMap}
          saveRule={saveRule}
          subsidiaries={subsidiaries}
        />
      )}
      {tab === 1 && (
        <ClassifierCoverageTab
          rows={classifierRows}
          loading={loading}
          error={error}
          notesMap={notesMap}
          setNotesMap={setNotesMap}
          saveRule={saveRule}
          llmRecs={llmRecs}
          setLlmRecs={setLlmRecs}
          llmLoading={llmLoading}
          fetchLlmRecs={fetchLlmRecs}
        />
      )}

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)}>
        {toast && <Alert severity={toast.severity}>{toast.msg}</Alert>}
      </Snackbar>
    </Box>
  );
}
